import React, { useEffect, useState } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { CalendarDays, ChevronDown, ChevronUp } from "lucide-react";
import { api } from "@/lib/api";
import { Skeleton } from "@/components/ui/skeleton";
import GironeBadge from "@/components/GironeBadge";

const GIRONI = ["A", "B", "C", "D"];

export default function Calendario() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openGiornata, setOpenGiornata] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/calendario");
        setData(data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const toggle = (g, j) => setOpenGiornata((s) => ({ ...s, [`${g}-${j}`]: !s[`${g}-${j}`] }));

  return (
    <div className="App">
      <Header />
      <div className="max-w-6xl mx-auto px-4 md:px-8 py-8 space-y-6">
        <div className="fade-up flex items-start gap-3">
          <div className="p-3 rounded-2xl" style={{ background: "var(--brand)", color: "white" }}>
            <CalendarDays className="w-7 h-7" />
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-slate-500">Stagione 2026/27</div>
            <h1 className="display font-black text-4xl md:text-5xl mt-1">
              Calendario <span style={{ color: "var(--brand)" }}>FIFE</span>
            </h1>
            <p className="text-slate-600 text-sm md:text-base mt-1">
              Tutte le giornate di Serie A del Fantacampionato divise per Girone.
            </p>
          </div>
        </div>

        {loading ? (
          <div className="space-y-3" data-testid="calendario-loading">
            {[0,1,2,3].map((i) => <Skeleton key={i} className="h-32 w-full rounded-2xl" />)}
          </div>
        ) : (
          <Tabs defaultValue="A" data-testid="calendario-tabs">
            <TabsList className="bg-slate-50 border border-slate-200 w-full sm:w-auto grid grid-cols-4 sm:inline-flex">
              {GIRONI.map((g) => (
                <TabsTrigger key={g} value={g} data-testid={`calendario-tab-${g}`}
                             className="data-[state=active]:bg-[var(--brand)] data-[state=active]:text-white inline-flex items-center gap-1.5 px-2 sm:px-3">
                  <GironeBadge girone={g} size={18} />
                  <span className="hidden sm:inline">Girone</span>
                </TabsTrigger>
              ))}
            </TabsList>

            {GIRONI.map((g) => {
              const giornate = (data && data[g]) || [];
              return (
                <TabsContent key={g} value={g} className="mt-6" data-testid={`calendario-content-${g}`}>
                  {giornate.length === 0 ? (
                    <div className="card-surface rounded-2xl p-8 text-center">
                      <div className="mx-auto mb-3 flex justify-center"><GironeBadge girone={g} size={48} /></div>
                      <div className="display font-bold text-lg text-slate-900">
                        Calendario Girone {g} non ancora disponibile
                      </div>
                      <p className="text-sm text-slate-600 mt-1">
                        L'amministratore caricherà a breve il calendario ufficiale.
                      </p>
                    </div>
                  ) : (
                    <GironeAccordion
                      girone={g}
                      giornate={giornate}
                      openGiornata={openGiornata}
                      toggle={toggle}
                    />
                  )}
                </TabsContent>
              );
            })}
          </Tabs>
        )}
      </div>
      <Footer />
    </div>
  );
}

function GironeAccordion({ girone, giornate, openGiornata, toggle }) {
  return (
    <div className="space-y-2" data-testid={`girone-${girone}-accordion`}>
      {giornate.map((gd) => {
        const key = `${girone}-${gd.giornata}`;
        const open = openGiornata[key];
        return (
          <div key={gd.giornata}
               className="rounded-2xl border border-slate-200 bg-white overflow-hidden"
               data-testid={`giornata-${girone}-${gd.giornata}`}>
            <button
              onClick={() => toggle(girone, gd.giornata)}
              className="w-full flex items-center justify-between px-4 md:px-5 py-3.5 hover:bg-slate-50 transition-colors"
              data-testid={`giornata-toggle-${girone}-${gd.giornata}`}
            >
              <div className="flex items-center gap-3 min-w-0">
                <div className="relative shrink-0">
                  <GironeBadge girone={girone} size={36} />
                  <span className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-slate-900 text-white text-[10px] font-black flex items-center justify-center border-2 border-white">
                    {gd.giornata}
                  </span>
                </div>
                <div className="text-left min-w-0">
                  <div className="display font-bold text-slate-900 text-base md:text-lg leading-tight truncate">
                    {gd.giornata}ª Giornata FIFE
                  </div>
                  {gd.giornata_serie_a && (
                    <div className="text-xs text-slate-500">
                      Riferimento: {gd.giornata_serie_a}ª Serie A
                    </div>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <span className="text-xs text-slate-500 hidden sm:inline">
                  {gd.matches.length} partite
                </span>
                {open ? <ChevronUp className="w-5 h-5 text-slate-400" />
                      : <ChevronDown className="w-5 h-5 text-slate-400" />}
              </div>
            </button>
            {open && (
              <div className="border-t border-slate-100 divide-y divide-slate-100">
                {gd.matches.map((m, i) => (
                  <MatchRow key={i} match={m} />
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function MatchRow({ match }) {
  const played = match.status === "played" && match.home_score != null && match.away_score != null;
  const hs = played ? Number(match.home_score) : null;
  const as_ = played ? Number(match.away_score) : null;
  const hWin = played && hs > as_;
  const aWin = played && as_ > hs;
  return (
    <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-2 md:gap-4 px-4 md:px-5 py-3">
      <div className={`font-semibold text-right text-sm md:text-base truncate ${hWin ? "text-slate-900" : played ? "text-slate-400" : "text-slate-900"}`} title={match.home}>
        {match.home}
      </div>
      {played ? (
        <div className="display font-black tabular-nums text-sm md:text-base px-2 py-1 rounded-md bg-emerald-50 border border-emerald-200 text-slate-900 select-none">
          <span className={hWin ? "text-emerald-700" : ""}>{hs}</span>
          <span className="mx-1.5 text-slate-400">–</span>
          <span className={aWin ? "text-emerald-700" : ""}>{as_}</span>
        </div>
      ) : (
        <div className="text-slate-400 font-mono text-xs md:text-sm px-2 py-1 rounded-md bg-slate-50 border border-slate-200 select-none">
          vs
        </div>
      )}
      <div className={`font-semibold text-left text-sm md:text-base truncate ${aWin ? "text-slate-900" : played ? "text-slate-400" : "text-slate-900"}`} title={match.away}>
        {match.away}
      </div>
      {played && (match.home_totale != null || match.away_totale != null) && (
        <div className="col-span-3 text-center text-[10px] uppercase tracking-widest text-slate-400 mt-1">
          {match.home_totale != null ? Number(match.home_totale).toFixed(1) : "-"} · {match.away_totale != null ? Number(match.away_totale).toFixed(1) : "-"}
        </div>
      )}
    </div>
  );
}
